import type { NativeSelectOption } from '@shared/ui/fields'
import type { Status as BookingStatus } from '@brighterly/lib-core-types/booking/enums'
import type { LessonStatusReasons } from './lesson-status'

import { Type as BookingType } from '@brighterly/lib-core-types/booking/enums'

import { useListsStore } from '@shared/model'

import { useLessonEditStore } from './edit/store'
import { useLessonEditSave } from './lesson-edit-save.hook'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { useLessonTutorSelect } from './lesson-tutor-select.hook'
import { useDisciplinePrivileges } from '../discipline-privileges.hook'
import { getLessonTutorName } from './lessons'
import {
  buildLessonStatusOptions,
  buildReasonCategoryOptions,
  buildReasonOptions,
  getFirstReason,
  getLessonStatusChoices,
  isLessonReasonStatus,
  isLessonTimeLocked,
} from './lesson-status'

interface LessonEditSingleReturn {
  refCanEdit: ComputedRef<boolean>
  refDate: ComputedRef<string>
  refStartOptions: ComputedRef<NativeSelectOption[]>
  refStartTime: ComputedRef<string>
  refDurationOptions: ComputedRef<NativeSelectOption[]>
  refDuration: ComputedRef<number | null>
  refStatusOptions: ComputedRef<NativeSelectOption[]>
  refStatus: ComputedRef<BookingStatus | null>
  refIsReasonShown: ComputedRef<boolean>
  refReasonCategoryOptions: ComputedRef<NativeSelectOption[]>
  refReasonCategory: ComputedRef<string | null>
  refReasonOptions: ComputedRef<NativeSelectOption[]>
  refReason: ComputedRef<string | null>
  refIsTimeLocked: ComputedRef<boolean>
  refTutorName: ComputedRef<string | null>
  refTutorScheduleUrl: ComputedRef<string>
  refIsTutorScheduleDisabled: ComputedRef<boolean>
  refChangeTutorText: ComputedRef<string>
  refCanChangeTutor: ComputedRef<boolean>
  refIsSaving: ComputedRef<boolean>
  refLessonsUrl: string
  setDate: (value: string) => void
  setStartTime: (option: NativeSelectOption) => void
  setDuration: (option: NativeSelectOption) => void
  setStatus: (option: NativeSelectOption) => void
  setReasonCategory: (option: NativeSelectOption) => void
  setReason: (option: NativeSelectOption) => void
  openTutorModal: () => void
  submit: () => Promise<void>
}

/** A select must show the lesson's own value even when the slots do not offer it (a 15:30 start, a 30 min lesson). */
const withCurrent = <T extends string | number>(values: T[], current: T | null): T[] =>
  current === null || values.includes(current) ? values : [current, ...values]

/**
 * The edit form: date, time, status with its reason, and the lesson's tutor beside them. Nothing here asks the API
 * until Save: a lesson moved to another time, or a tutor the admin wants to change, goes through the tutor modal,
 * which reads the tutors for that time and the filters set there.
 */
export const useLessonEditSingle = (): LessonEditSingleReturn => {
  const store = useLessonEditStore()
  const listsStore = useListsStore()
  const { canManageBookings, canManageDemoBookings, canViewTutorsSchedule } = useLessonPrivileges()
  const { canManageDisciplines } = useDisciplinePrivileges()
  const { refLessonsUrl, save } = useLessonEditSave()

  const refStatusReasons = computed<LessonStatusReasons>(() =>
    (listsStore.getLists?.booking.change_status_reasons ?? {}) as LessonStatusReasons)

  /** The API needs the discipline right and the right for the lesson's type. */
  const refCanEdit = computed<boolean>(() => {
    const lesson = store.getLesson
    if (!lesson || !canManageDisciplines.value) return false

    return lesson.type === BookingType.Paid ? canManageBookings.value : canManageDemoBookings.value
  })

  const refDate = computed<string>(() => store.getDate)

  const refStartTime = computed<string>(() => store.getStartTime)

  const refStartOptions = computed<NativeSelectOption[]>(() =>
    withCurrent(store.getSlots?.start ?? [], refStartTime.value || null).map(slot => ({
      id: slot,
      value: slot,
      text: slot,
      selected: slot === refStartTime.value,
    })))

  const refDuration = computed<number | null>(() => store.getDuration)

  const refDurationOptions = computed<NativeSelectOption[]>(() =>
    withCurrent(store.getSlots?.duration ?? [], refDuration.value).map(minutes => ({
      id: minutes,
      value: minutes,
      text: `${minutes} min`,
      selected: minutes === refDuration.value,
    })))

  const refStatus = computed<BookingStatus | null>(() => store.getStatus)

  const refStatusOptions = computed<NativeSelectOption[]>(() => {
    const lesson = store.getLesson
    const labels = listsStore.getLists?.booking.status ?? {}
    if (!lesson) return []

    return buildLessonStatusOptions(labels, getLessonStatusChoices(labels, lesson.status), refStatus.value)
  })

  const refIsReasonShown = computed<boolean>(() => isLessonReasonStatus(refStatus.value))

  const refReasonCategory = computed<string | null>(() => store.getReasonCategory)

  const refReasonCategoryOptions = computed<NativeSelectOption[]>(() =>
    buildReasonCategoryOptions(refStatusReasons.value, listsStore.getLists?.booking.reason_category ?? {}, refReasonCategory.value))

  const refReason = computed<string | null>(() => store.getReason)

  const refReasonOptions = computed<NativeSelectOption[]>(() =>
    buildReasonOptions(refStatusReasons.value, refReasonCategory.value, refReason.value))

  const refIsTimeLocked = computed<boolean>(() => isLessonTimeLocked(refStatus.value))

  const refIsSaving = computed<boolean>(() => store.isSaving)

  const refCanChangeTutor = computed<boolean>(() =>
    refCanEdit.value && canViewTutorsSchedule.value && !refIsTimeLocked.value && !refIsSaving.value)

  const refTutorName = computed<string | null>(() => {
    const tutor = store.getLesson?.tutor
    if (!tutor) return null

    return getLessonTutorName(tutor, listsStore.getLists?.tutor.paid_label ?? {})
  })

  const { refScheduleUrl: refTutorScheduleUrl, refIsScheduleDisabled: refIsTutorScheduleDisabled } = useLessonTutorSelect({
    tutorId: computed(() => store.getLesson?.tutor_id ?? null),
    refOptions: computed(() => []),
    refDate,
    refIsLoading: refIsSaving,
  })

  const refChangeTutorText = computed<string>(() => (refTutorName.value ? 'Change tutor' : 'Select tutor'))

  const setDate = (value: string): void => {
    if (value) store.setDate(value)
  }

  const setStartTime = (option: NativeSelectOption): void => {
    store.setStartTime(String(option.value))
  }

  const setDuration = (option: NativeSelectOption): void => {
    store.setDuration(Number(option.value))
  }

  const setStatus = (option: NativeSelectOption): void => {
    store.setStatus(option.value as BookingStatus)
  }

  const setReasonCategory = (option: NativeSelectOption): void => {
    const category = option.value ? String(option.value) : null

    store.setReasonCategory(category)
    store.setReason(getFirstReason(refStatusReasons.value, category))
  }

  const setReason = (option: NativeSelectOption): void => {
    store.setReason(option.value ? String(option.value) : null)
  }

  const openTutorModal = (): void => {
    if (!refCanChangeTutor.value) return

    store.setTutorModalOpen(true)
  }

  /** A lesson with a tutor moved to another time is confirmed in the tutor modal, where the tutors for the new time
   *  are read: the tutor may be busy then. Every other change saves at once. */
  const submit = async (): Promise<void> => {
    const lesson = store.getLesson
    if (!lesson || refIsSaving.value) return

    const isMoved = store.getStart !== '' && store.getStart !== lesson.start

    if (isMoved && store.getTutorId !== null && refCanChangeTutor.value) {
      openTutorModal()
      return
    }

    await save()
  }

  return {
    refCanEdit,
    refDate,
    refStartOptions,
    refStartTime,
    refDurationOptions,
    refDuration,
    refStatusOptions,
    refStatus,
    refIsReasonShown,
    refReasonCategoryOptions,
    refReasonCategory,
    refReasonOptions,
    refReason,
    refIsTimeLocked,
    refTutorName,
    refTutorScheduleUrl,
    refIsTutorScheduleDisabled,
    refChangeTutorText,
    refCanChangeTutor,
    refIsSaving,
    refLessonsUrl,
    setDate,
    setStartTime,
    setDuration,
    setStatus,
    setReasonCategory,
    setReason,
    openTutorModal,
    submit,
  }
}
